'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ds/Button';
import { Input } from '@/components/ds/Input';
import { OTPInput } from '@/components/ds/OTPInput';
import { AuroraForm } from '@/components/onboarding/AuroraForm';

type Step = 'phone' | 'code';

export interface AddPhoneGateProps {
  /** Reuses DashboardLayout's own handleLogout — the gate's only way out. */
  onSignOut: () => void;
}

/**
 * Decision #29's blocking "add your phone" step. Mounted by
 * `dashboard/layout.tsx` in place of the whole shell whenever the caller's
 * own `auth.getProfile` reports `hasVerifiedPhone: false` — see that file for
 * why the layout boundary is what makes this cover every dashboard route.
 *
 * Phone -> OTP -> `auth.linkPhoneIdentity`, mirroring the public booking
 * page's phone/OTP UI shape (`(public)/b/[slug]/business-page.tsx`). Two real
 * differences from that reference:
 *   - `linkPhoneIdentity` is a `protectedProcedure` reached via the AUTHED
 *     `trpc` client (never `publicTrpc`) — the caller is already signed in.
 *   - It can additionally throw CONFLICT (this number already verified on a
 *     DIFFERENT account) — a dead end the owner resolves with another number,
 *     not a retry, so it gets its own copy rather than falling into the
 *     generic-error bucket.
 *
 * On success, invalidates `auth.getProfile` (never mutates the session —
 * linking mints no tokens) so the SAME query `DashboardLayout` reads flips to
 * `hasVerifiedPhone: true` and the gate unmounts itself on the next render.
 *
 * ## The shell is `AuroraForm`, like every other credential screen on web
 *
 * It was a `Card` on flat `--bg-canvas` with a 64px phone disc as its hero
 * mark. Every full-screen place on web that asks for a credential now wears
 * one surface (owner, 2026-08-20: "all the login pages should look the same in
 * the web, with the gradient look"), so this is the aurora field and the
 * lockup, same as `/login` and the wizard's sign-in.
 *
 * The phone disc went with the card. The house rule is one hero per surface,
 * and the lockup is it; the title already says this screen is about a phone
 * number, so the disc was a second mark saying the same thing.
 */
export function AddPhoneGate({ onSignOut }: AddPhoneGateProps) {
  const { t } = useTranslation();
  const utils = trpc.useUtils();

  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const requestOtpMut = trpc.auth.requestOtp.useMutation();
  const linkPhoneMut = trpc.auth.linkPhoneIdentity.useMutation();

  const handleSend = async () => {
    setError(null);
    setBusy(true);
    try {
      await requestOtpMut.mutateAsync({ phone });
      setCode('');
      setStep('code');
    } catch (err: unknown) {
      const e = err as { data?: { code?: string } };
      setError(
        e?.data?.code === 'TOO_MANY_REQUESTS'
          ? t('phoneGate.rate_limited')
          : t('phoneGate.generic_error'),
      );
    } finally {
      setBusy(false);
    }
  };

  const handleVerify = async () => {
    setError(null);
    setBusy(true);
    try {
      await linkPhoneMut.mutateAsync({ phone, code });
      // A LINK, not a login — nothing about the session changes. Invalidating
      // is what releases the gate: DashboardLayout's own `auth.getProfile`
      // query shares this cache key and refetches, sees `hasVerifiedPhone:
      // true`, and stops rendering this component.
      await utils.auth.getProfile.invalidate();
    } catch (err: unknown) {
      const e = err as { data?: { code?: string } };
      if (e?.data?.code === 'UNAUTHORIZED') {
        setError(t('phoneGate.wrong_code'));
      } else if (e?.data?.code === 'CONFLICT') {
        setError(t('phoneGate.already_linked'));
      } else if (e?.data?.code === 'TOO_MANY_REQUESTS') {
        setError(t('phoneGate.rate_limited'));
      } else {
        setError(t('phoneGate.generic_error'));
      }
    } finally {
      setBusy(false);
    }
  };

  const handleChangeNumber = () => {
    setStep('phone');
    setError(null);
    setCode('');
  };

  return (
    <AuroraForm
      testId="phone-gate"
      title={t('phoneGate.title')}
      subtitle={t('phoneGate.subtitle')}
      // On the code step the quiet link changes the number, which is this
      // screen's own "go back". Signing out is NOT that link: it is the way
      // out of the gate entirely, is available on both steps, and sits below
      // a hairline so the two are never mistaken for each other.
      backLabel={step === 'code' ? t('phoneGate.change_number') : undefined}
      onBack={step === 'code' ? handleChangeNumber : undefined}
    >
      {error && (
        <p role="alert" style={{ color: 'var(--error)', margin: 0 }}>
          {error}
        </p>
      )}

      {step === 'phone' && (
        <>
          <Input
            label={t('phoneGate.phone_label')}
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder={t('phoneGate.phone_placeholder')}
            latin
            data-testid="phone-gate-phone-input"
          />
          <Button
            variant="primary"
            fullWidth
            onClick={handleSend}
            disabled={busy || !phone.trim()}
            aria-busy={busy}
          >
            {busy ? t('phoneGate.sending') : t('phoneGate.send_code')}
          </Button>
        </>
      )}

      {step === 'code' && (
        <>
          <OTPInput
            value={code}
            state={error ? 'error' : 'default'}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            aria-label={t('phoneGate.code_label')}
            data-testid="phone-gate-otp-input"
          />
          <Button
            variant="primary"
            fullWidth
            onClick={handleVerify}
            disabled={busy || code.length < 6}
            aria-busy={busy}
          >
            {busy ? t('phoneGate.verifying') : t('phoneGate.verify')}
          </Button>
        </>
      )}

      <div
        style={{
          textAlign: 'center',
          marginBlockStart: 'var(--sp-2)',
          paddingBlockStart: 'var(--sp-6)',
          borderBlockStart: '1px solid var(--border-subtle)',
        }}
      >
        <Button variant="ghost" size="sm" onClick={onSignOut}>
          {t('common.logout')}
        </Button>
      </div>
    </AuroraForm>
  );
}
